import React, { useState, Children, Fragment } from 'react';
import { BuildingIcon, MapPinIcon, UsersIcon, PhoneIcon, MailIcon, EditIcon, XIcon, PlusIcon, FileTextIcon, ShieldIcon, CoinsIcon, HashIcon, ArrowUpIcon, ArrowDownIcon, GitBranchIcon, EyeIcon, ImageIcon } from 'lucide-react';
import { AddressList } from '../AddressList';
import { AddressForm } from '../AddressForm';
import { ContactForm } from '../../financial/ContactForm';
interface OrgUnitDetailProps {
  orgUnit: any;
  onEdit: (orgUnit: any) => void;
  onClose: () => void;
  locale: string;
}
// Mock centralized contacts data - in real app, this would come from context/API
const initialCentralizedContacts = [{
  id: 'con-001',
  name: 'Sarah Chen',
  email: 'sarah.chen@apactech.com.au',
  phone: '+61 2 9876 5432',
  role: 'Site Manager',
  isPrimary: true,
  associations: [{
    type: 'Org Unit',
    name: 'Sydney Office',
    id: 'org-007'
  }, {
    type: 'Vendor',
    name: 'APAC Tech Supplies',
    id: 'vendor-005'
  }]
}, {
  id: 'con-002',
  name: 'Michael Wong',
  email: 'michael.wong@apactech.com.au',
  phone: '+61 2 9876 5400',
  role: 'Managing Director',
  isPrimary: true,
  associations: [{
    type: 'Org Unit',
    name: 'Asia Pacific Technologies Pty Ltd',
    id: 'org-006'
  }]
}, {
  id: 'con-003',
  name: 'Jane Doe',
  email: 'jane.doe@globaltech.com',
  phone: '+1 (555) 987-6543',
  role: 'CEO',
  isPrimary: true,
  associations: [{
    type: 'Org Unit',
    name: 'Global Technologies Inc.',
    id: 'org-001'
  }]
}, {
  id: 'con-004',
  name: 'John Smith',
  email: 'john.smith@globaltech.com',
  phone: '+1 (555) 123-4567',
  role: 'Site Manager',
  isPrimary: true,
  associations: [{
    type: 'Org Unit',
    name: 'New York Headquarters',
    id: 'org-002'
  }]
}, {
  id: 'con-013',
  name: 'Tom Anderson',
  email: 'tom.anderson@globaltech.com',
  phone: '+1 (555) 234-5678',
  role: 'Finance Manager',
  isPrimary: false,
  associations: [{
    type: 'Org Unit',
    name: 'Finance Department',
    id: 'org-005'
  }]
}, {
  id: 'con-014',
  name: 'Emma Wilson',
  email: 'emma.wilson@globaltech.com',
  phone: '+1 (555) 345-6789',
  role: 'Operations Director',
  isPrimary: false,
  associations: [{
    type: 'Org Unit',
    name: 'San Francisco Office',
    id: 'org-003'
  }]
}];
export function OrgUnitDetail({
  orgUnit,
  onEdit,
  onClose,
  locale
}: OrgUnitDetailProps) {
  const [activeTab, setActiveTab] = useState('overview');
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [centralizedContacts, setCentralizedContacts] = useState(initialCentralizedContacts);
  const [orgUnitContactIds, setOrgUnitContactIds] = useState(orgUnit.contactIds || []);
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  const getOrgUnitIcon = (typeCode: string, size: string = 'h-5 w-5') => {
    switch (typeCode) {
      case 'ENTITY':
        return <BuildingIcon className={`${size} text-indigo-600`} data-id="element-19888" />;
      case 'SITE':
        return <MapPinIcon className={`${size} text-green-600`} data-id="element-19889" />;
      case 'DEPT':
        return <UsersIcon className={`${size} text-blue-600`} data-id="element-19890" />;
      default:
        return <BuildingIcon className={`${size} text-gray-600`} data-id="element-19891" />;
    }
  };
  // Get contacts from centralized system using contactIds
  const getContactsFromIds = (contactIds: string[] = []) => {
    return contactIds.map(id => centralizedContacts.find(c => c.id === id)).filter(Boolean);
  };
  const contacts = getContactsFromIds(orgUnitContactIds);
  const handleAddContact = () => {
    setEditingContact(null);
    setShowContactForm(true);
  };
  const handleEditContact = (contact: any) => {
    setEditingContact(contact);
    setShowContactForm(true);
  };
  const handleSaveContact = (contactData: any) => {
    if (contactData.id) {
      // Update existing contact in centralized store
      setCentralizedContacts(centralizedContacts.map(c => {
        if (c.id === contactData.id) {
          // Update contact data but preserve associations
          return {
            ...c,
            ...contactData,
            associations: c.associations
          };
        }
        return c;
      }));
    } else {
      // Create new contact and add to centralized store
      const newContact = {
        ...contactData,
        id: `con-${Date.now()}`,
        associations: [{
          type: 'Org Unit',
          name: orgUnit.name,
          id: orgUnit.id
        }]
      };
      setCentralizedContacts([...centralizedContacts, newContact]);
      // Add to this org unit's contact list
      setOrgUnitContactIds([...orgUnitContactIds, newContact.id]);
    }
    setShowContactForm(false);
    setEditingContact(null);
  };
  const handleRemoveContact = (contactId: string) => {
    if (window.confirm('Remove this contact from this org unit?')) {
      // Remove from org unit's contact list
      setOrgUnitContactIds(orgUnitContactIds.filter(id => id !== contactId));
      // Update centralized contact to remove this association
      setCentralizedContacts(centralizedContacts.map(c => {
        if (c.id === contactId) {
          return {
            ...c,
            associations: c.associations.filter(assoc => assoc.id !== orgUnit.id)
          };
        }
        return c;
      }));
    }
  };
  return <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-19892">
      {/* Header */}
      <div className="px-6 py-5 border-b border-gray-200" data-id="element-19893">
        <div className="flex items-center justify-between" data-id="element-19894">
          <div className="flex items-center space-x-4" data-id="element-19895">
            {/* Logo Display */}
            {orgUnit.logoUrl ? <div className="flex-shrink-0 flex items-center justify-center" data-id="element-19896">
                <img src={orgUnit.logoUrl} alt={`${orgUnit.name} logo`} className="max-h-16 max-w-32 object-contain rounded-lg border-2 border-gray-200 bg-white p-2" data-id="element-19897" />
              </div> : <div className="flex-shrink-0" data-id="element-19898">
                {getOrgUnitIcon(orgUnit.orgUnitType.code, 'h-8 w-8')}
              </div>}
            <div data-id="element-19899">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-19900">
                {orgUnit.name}
              </h3>
              <p className="text-sm text-gray-500" data-id="element-19901">
                {orgUnit.code} • {orgUnit.orgUnitType.name}
              </p>
            </div>
          </div>
          <div className="flex space-x-2" data-id="element-19902">
            <button onClick={() => onEdit(orgUnit)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19903">
              <EditIcon className="h-4 w-4 mr-1" data-id="element-19904" />
              Edit
            </button>
            <button onClick={onClose} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19905">
              <XIcon className="h-4 w-4" data-id="element-19906" />
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200" data-id="element-19907">
        <nav className="-mb-px flex space-x-8 px-6" data-id="element-19908">
          <button onClick={() => setActiveTab('overview')} className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'overview' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-19909">
            Overview
          </button>
          <button onClick={() => setActiveTab('hierarchy')} className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'hierarchy' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-19910">
            <GitBranchIcon className="h-4 w-4 inline mr-1" data-id="element-19911" />
            Hierarchy
          </button>
          <button onClick={() => setActiveTab('addresses')} className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'addresses' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-19912">
            Addresses ({orgUnit.addresses?.length || 0})
          </button>
          <button onClick={() => setActiveTab('contacts')} className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'contacts' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-19913">
            Contacts ({contacts.length})
          </button>
          <button onClick={() => setActiveTab('registrations')} className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'registrations' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-19914">
            Registrations ({orgUnit.registrations?.length || 0})
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="px-6 py-5" data-id="element-19915">
        {activeTab === 'overview' && <div className="space-y-6" data-id="element-19916">
            {/* Logo Section */}
            <div data-id="element-19917">
              <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-19918">
                Organisation Logo
              </h4>
              {orgUnit.logoUrl ? <div className="flex items-center space-x-4" data-id="element-19919">
                  <div className="flex items-center justify-center" data-id="element-19920">
                    <img src={orgUnit.logoUrl} alt={`${orgUnit.name} logo`} className="max-h-24 max-w-48 object-contain rounded-lg border-2 border-gray-200 bg-white p-2" data-id="element-19921" />
                  </div>
                  <div className="text-sm text-gray-500" data-id="element-19922">
                    <p className="font-medium text-gray-700" data-id="element-19923">Logo uploaded</p>
                    <p className="text-xs mt-1" data-id="element-19924">
                      Click Edit to change or remove the logo
                    </p>
                  </div>
                </div> : <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300" data-id="element-19925">
                  <ImageIcon className="h-12 w-12 text-gray-400" data-id="element-19926" />
                  <div className="text-sm text-gray-500" data-id="element-19927">
                    <p className="font-medium text-gray-700" data-id="element-19928">
                      No logo uploaded
                    </p>
                    <p className="text-xs mt-1" data-id="element-19929">
                      Click Edit to upload an organisation logo
                    </p>
                  </div>
                </div>}
            </div>

            {/* Basic Information */}
            <div data-id="element-19930">
              <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-19931">
                Basic Information
              </h4>
              <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2" data-id="element-19932">
                <div data-id="element-19933">
                  <dt className="text-sm font-medium text-gray-500" data-id="element-19934">Code</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-mono" data-id="element-19935">
                    {orgUnit.code}
                  </dd>
                </div>
                <div data-id="element-19936">
                  <dt className="text-sm font-medium text-gray-500" data-id="element-19937">Name</dt>
                  <dd className="mt-1 text-sm text-gray-900" data-id="element-19938">{orgUnit.name}</dd>
                </div>
                <div data-id="element-19939">
                  <dt className="text-sm font-medium text-gray-500" data-id="element-19940">Type</dt>
                  <dd className="mt-1 text-sm text-gray-900" data-id="element-19941">
                    {orgUnit.orgUnitType.name}
                  </dd>
                </div>
                <div data-id="element-19942">
                  <dt className="text-sm font-medium text-gray-500" data-id="element-19943">Status</dt>
                  <dd className="mt-1" data-id="element-19944">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${orgUnit.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-19945">
                      {orgUnit.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </dd>
                </div>
                {orgUnit.description && <div className="sm:col-span-2" data-id="element-19946">
                    <dt className="text-sm font-medium text-gray-500" data-id="element-19947">
                      Description
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900" data-id="element-19948">
                      {orgUnit.description}
                    </dd>
                  </div>}
              </dl>
            </div>

            {/* Hierarchy Information */}
            <div data-id="element-19949">
              <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-19950">
                Hierarchy
              </h4>
              <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2" data-id="element-19951">
                <div data-id="element-19952">
                  <dt className="text-sm font-medium text-gray-500" data-id="element-19953">Path</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-mono" data-id="element-19954">
                    {orgUnit.path}
                  </dd>
                </div>
                <div data-id="element-19955">
                  <dt className="text-sm font-medium text-gray-500" data-id="element-19956">Depth</dt>
                  <dd className="mt-1 text-sm text-gray-900" data-id="element-19957">
                    {orgUnit.depth}
                  </dd>
                </div>
                <div data-id="element-19958">
                  <dt className="text-sm font-medium text-gray-500" data-id="element-19959">
                    Organisation Root
                  </dt>
                  <dd className="mt-1" data-id="element-19960">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${orgUnit.isOrgRoot ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-19961">
                      {orgUnit.isOrgRoot ? 'Yes' : 'No'}
                    </span>
                  </dd>
                </div>
              </dl>
            </div>

            {/* GL Coding */}
            {orgUnit.participatesInGLCoding && <div data-id="element-19962">
                <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-19963">
                  GL Coding
                </h4>
                <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2" data-id="element-19964">
                  <div data-id="element-19965">
                    <dt className="text-sm font-medium text-gray-500" data-id="element-19966">
                      GL Code Segment
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 font-mono" data-id="element-19967">
                      {orgUnit.glCodeSegment || 'Not set'}
                    </dd>
                  </div>
                  <div data-id="element-19968">
                    <dt className="text-sm font-medium text-gray-500" data-id="element-19969">
                      Participates in GL Coding
                    </dt>
                    <dd className="mt-1" data-id="element-19970">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-19971">
                        Yes
                      </span>
                    </dd>
                  </div>
                </dl>
              </div>}

            {/* Metadata */}
            <div data-id="element-19972">
              <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-19973">
                Metadata
              </h4>
              <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2" data-id="element-19974">
                <div data-id="element-19975">
                  <dt className="text-sm font-medium text-gray-500" data-id="element-19976">Created</dt>
                  <dd className="mt-1 text-sm text-gray-900" data-id="element-19977">
                    {formatDate(orgUnit.createdUtc)}
                  </dd>
                </div>
                {orgUnit.updatedUtc && <div data-id="element-19978">
                    <dt className="text-sm font-medium text-gray-500" data-id="element-19979">
                      Last Updated
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900" data-id="element-19980">
                      {formatDate(orgUnit.updatedUtc)}
                    </dd>
                  </div>}
              </dl>
            </div>
          </div>}

        {activeTab === 'hierarchy' && <div className="space-y-6" data-id="element-19981">
            {/* Visual Hierarchy Diagram */}
            <div data-id="element-19982">
              <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-19983">
                Organisation Hierarchy
              </h4>

              {/* Parent Section */}
              {orgUnit.parentOrgUnitId && <div className="mb-8" data-id="element-19984">
                  <div className="flex items-center mb-3" data-id="element-19985">
                    <ArrowUpIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-19986" />
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide" data-id="element-19987">
                      Parent
                    </span>
                  </div>
                  <div className="relative" data-id="element-19988">
                    {/* Connecting line */}
                    <div className="absolute left-8 top-12 bottom-0 w-0.5 bg-gray-200" data-id="element-19989"></div>

                    <div className="bg-gradient-to-r from-gray-50 to-white border-2 border-gray-200 rounded-lg p-4 hover:border-indigo-300 transition-colors" data-id="element-19990">
                      <div className="flex items-center justify-between" data-id="element-19991">
                        <div className="flex items-center flex-1" data-id="element-19992">
                          <div className="flex-shrink-0" data-id="element-19993">
                            {getOrgUnitIcon('ENTITY', 'h-6 w-6')}
                          </div>
                          <div className="ml-3 flex-1" data-id="element-19994">
                            <p className="text-sm font-medium text-gray-900" data-id="element-19995">
                              Parent OrgUnit Name
                            </p>
                            <p className="text-xs text-gray-500 font-mono" data-id="element-19996">
                              PARENT-CODE • Legal Entity
                            </p>
                          </div>
                        </div>
                        <button className="p-1 text-green-600 hover:text-green-900 hover:bg-green-50 rounded" title="View" data-id="element-19997">
                          <EyeIcon className="h-4 w-4" data-id="element-19998" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>}

              {/* Current OrgUnit */}
              <div className="mb-8" data-id="element-19999">
                <div className="flex items-center mb-3" data-id="element-20000">
                  <div className="h-4 w-4 rounded-full bg-indigo-600 mr-2" data-id="element-20001"></div>
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide" data-id="element-20002">
                    Current
                  </span>
                </div>
                <div className="bg-gradient-to-r from-indigo-50 to-white border-2 border-indigo-500 rounded-lg p-4 shadow-sm" data-id="element-20003">
                  <div className="flex items-center" data-id="element-20004">
                    <div className="flex-shrink-0" data-id="element-20005">
                      {getOrgUnitIcon(orgUnit.orgUnitType.code, 'h-8 w-8')}
                    </div>
                    <div className="ml-3 flex-1" data-id="element-20006">
                      <p className="text-base font-semibold text-gray-900" data-id="element-20007">
                        {orgUnit.name}
                      </p>
                      <p className="text-sm text-gray-600 font-mono" data-id="element-20008">
                        {orgUnit.code} • {orgUnit.orgUnitType.name}
                      </p>
                      <p className="text-xs text-gray-500 mt-1 font-mono" data-id="element-20009">
                        Path: {orgUnit.path}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Children Section */}
              {orgUnit.childOrgUnits && orgUnit.childOrgUnits.length > 0 && <div data-id="element-20010">
                  <div className="flex items-center mb-3" data-id="element-20011">
                    <ArrowDownIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-20012" />
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide" data-id="element-20013">
                      Children ({orgUnit.childOrgUnits.length})
                    </span>
                  </div>
                  <div className="space-y-3 relative" data-id="element-20014">
                    {/* Connecting lines for children */}
                    <div className="absolute left-8 top-0 bottom-3 w-0.5 bg-gray-200" data-id="element-20015"></div>

                    {orgUnit.childOrgUnits.map((child: any, index: number) => <div key={child.id} className="relative" data-id="element-20016">
                        {/* Horizontal connector */}
                        <div className="absolute left-8 top-6 w-6 h-0.5 bg-gray-200" data-id="element-20017"></div>

                        <div className="ml-14 bg-white border border-gray-200 rounded-lg p-3 hover:border-green-300 hover:shadow-sm transition-all" data-id="element-20018">
                          <div className="flex items-center justify-between" data-id="element-20019">
                            <div className="flex items-center flex-1" data-id="element-20020">
                              <div className="flex-shrink-0" data-id="element-20021">
                                {getOrgUnitIcon(child.orgUnitType.code, 'h-5 w-5')}
                              </div>
                              <div className="ml-3 flex-1" data-id="element-20022">
                                <p className="text-sm font-medium text-gray-900" data-id="element-20023">
                                  {child.name}
                                </p>
                                <p className="text-xs text-gray-500 font-mono" data-id="element-20024">
                                  {child.code} • {child.orgUnitType.name}
                                </p>
                                {child.childOrgUnits && child.childOrgUnits.length > 0 && <p className="text-xs text-gray-400 mt-1" data-id="element-20025">
                                      {child.childOrgUnits.length} child
                                      {child.childOrgUnits.length !== 1 ? 'ren' : ''}
                                    </p>}
                              </div>
                            </div>
                            <button className="p-1 text-green-600 hover:text-green-900 hover:bg-green-50 rounded" title="View" data-id="element-20026">
                              <EyeIcon className="h-4 w-4" data-id="element-20027" />
                            </button>
                          </div>
                        </div>
                      </div>)}
                  </div>
                </div>}

              {/* No children message */}
              {(!orgUnit.childOrgUnits || orgUnit.childOrgUnits.length === 0) && <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200" data-id="element-20028">
                  <GitBranchIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" data-id="element-20029" />
                  <p className="text-sm text-gray-500" data-id="element-20030">
                    No child organisation units
                  </p>
                  <button className="mt-3 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-20031">
                    <PlusIcon className="h-4 w-4 mr-1" data-id="element-20032" />
                    Add Child OrgUnit
                  </button>
                </div>}
            </div>

            {/* Path Breadcrumb */}
            <div className="pt-6 border-t border-gray-200" data-id="element-20033">
              <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-20034">
                Full Path
              </h4>
              <div className="flex items-center space-x-2 text-sm" data-id="element-20035">
                {orgUnit.path.split('/').filter(Boolean).map((segment: string, index: number, array: string[]) => <Fragment key={index} data-id="element-20036">
                      <span className={`${index === array.length - 1 ? 'font-semibold text-indigo-600' : 'text-gray-600'}`} data-id="element-20037">
                        {segment}
                      </span>
                      {index < array.length - 1 && <span className="text-gray-400" data-id="element-20038">/</span>}
                    </Fragment>)}
              </div>
            </div>
          </div>}

        {activeTab === 'addresses' && <div data-id="element-20039">
            <div className="flex justify-between items-center mb-4" data-id="element-20040">
              <h4 className="text-sm font-medium text-gray-900" data-id="element-20041">Addresses</h4>
              <button onClick={() => setShowAddressForm(true)} className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-20042">
                <PlusIcon className="h-4 w-4 mr-1" data-id="element-20043" />
                Add Address
              </button>
            </div>
            <AddressList addresses={orgUnit.addresses || []} onEdit={address => console.log('Edit address:', address)} onDelete={addressId => console.log('Delete address:', addressId)} locale={locale} data-id="element-20044" />
          </div>}

        {activeTab === 'contacts' && <div data-id="element-20045">
            <div className="flex justify-between items-center mb-4" data-id="element-20046">
              <h4 className="text-sm font-medium text-gray-900" data-id="element-20047">Contacts</h4>
              <button onClick={handleAddContact} className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-20048">
                <PlusIcon className="h-4 w-4 mr-1" data-id="element-20049" />
                Add Contact
              </button>
            </div>
            {contacts.length > 0 ? <div className="space-y-3" data-id="element-20050">
                {contacts.map((contact: any) => <div key={contact.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-20051">
                    <div className="flex items-start justify-between" data-id="element-20052">
                      <div className="flex items-start flex-1" data-id="element-20053">
                        <div className="flex-shrink-0" data-id="element-20054">
                          <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center" data-id="element-20055">
                            <UsersIcon className="h-5 w-5 text-indigo-600" data-id="element-20056" />
                          </div>
                        </div>
                        <div className="ml-3 flex-1" data-id="element-20057">
                          <div className="flex items-center gap-2" data-id="element-20058">
                            <p className="text-sm font-medium text-gray-900" data-id="element-20059">
                              {contact.name}
                            </p>
                            {contact.isPrimary && <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-20060">
                                Primary
                              </span>}
                          </div>
                          {contact.role && <p className="text-sm text-gray-500" data-id="element-20061">
                              {contact.role}
                            </p>}
                          <div className="mt-1 flex items-center text-sm text-gray-500" data-id="element-20062">
                            <MailIcon className="h-4 w-4 mr-1" data-id="element-20063" />
                            {contact.email}
                          </div>
                          {contact.phone && <div className="mt-1 flex items-center text-sm text-gray-500" data-id="element-20064">
                              <PhoneIcon className="h-4 w-4 mr-1" data-id="element-20065" />
                              {contact.phone}
                            </div>}
                        </div>
                      </div>
                      <div className="flex gap-2 ml-4" data-id="element-20066">
                        <button onClick={() => handleEditContact(contact)} className="text-indigo-600 hover:text-indigo-900" title="Edit contact" data-id="element-20067">
                          <EditIcon className="h-4 w-4" data-id="element-20068" />
                        </button>
                        <button onClick={() => handleRemoveContact(contact.id)} className="text-red-600 hover:text-red-900" title="Remove contact" data-id="element-20069">
                          <XIcon className="h-4 w-4" data-id="element-20070" />
                        </button>
                      </div>
                    </div>
                  </div>)}
              </div> : <div className="text-center py-8 bg-blue-50 rounded-lg border-2 border-blue-200" data-id="element-20071">
                <UsersIcon className="mx-auto h-12 w-12 text-blue-400" data-id="element-20072" />
                <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-20073">
                  No contacts associated
                </h3>
                <p className="mt-1 text-sm text-gray-500" data-id="element-20074">
                  Get started by adding a contact to this org unit.
                </p>
                <button onClick={handleAddContact} className="mt-3 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-20075">
                  <PlusIcon className="h-4 w-4 mr-1" data-id="element-20076" />
                  Add Contact
                </button>
              </div>}
          </div>}

        {activeTab === 'registrations' && <div data-id="element-20077">
            <div className="flex justify-between items-center mb-4" data-id="element-20078">
              <h4 className="text-sm font-medium text-gray-900" data-id="element-20079">
                Registrations
              </h4>
              <button className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-20080">
                <PlusIcon className="h-4 w-4 mr-1" data-id="element-20081" />
                Add Registration
              </button>
            </div>
            {orgUnit.registrations && orgUnit.registrations.length > 0 ? <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-20082">
                <ul className="divide-y divide-gray-200" data-id="element-20083">
                  {orgUnit.registrations.map((reg: any) => <li key={reg.id} className="px-4 py-4" data-id="element-20084">
                      <div className="flex items-center justify-between" data-id="element-20085">
                        <div className="flex-1" data-id="element-20086">
                          <div className="flex items-center" data-id="element-20087">
                            <FileTextIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-20088" />
                            <div data-id="element-20089">
                              <p className="text-sm font-medium text-gray-900" data-id="element-20090">
                                {reg.type}
                              </p>
                              <p className="text-sm text-gray-500 font-mono" data-id="element-20091">
                                {reg.value}
                              </p>
                            </div>
                          </div>
                        </div>
                        {reg.validFrom && <div className="text-sm text-gray-500" data-id="element-20092">
                            Valid from: {formatDate(reg.validFrom)}
                          </div>}
                      </div>
                    </li>)}
                </ul>
              </div> : <div className="text-center py-8 text-gray-500" data-id="element-20093">
                No registrations added yet.
              </div>}
          </div>}
      </div>

      {/* Address Form Modal */}
      {showAddressForm && <AddressForm onClose={() => setShowAddressForm(false)} onSave={address => {
      console.log('Saving address:', address);
      setShowAddressForm(false);
    }} locale={locale} data-id="element-20094" />}

      {/* Contact Form Modal */}
      {showContactForm && <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true" data-id="element-20095">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-20096">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => setShowContactForm(false)} data-id="element-20097"></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-20098">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-20099">
              <ContactForm contact={editingContact} onSave={handleSaveContact} onCancel={() => {
            setShowContactForm(false);
            setEditingContact(null);
          }} locale={locale} data-id="element-20100" />
            </div>
          </div>
        </div>}
    </div>;
}